import React from 'react';
import { Download, File, FileText, Image as ImageIcon, FileSpreadsheet, User } from 'lucide-react';
import StatusBadge from './StatusBadge';

const CommentThread = ({ comments = [], statusHistory = [], currentUser }) => {
  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
      return <ImageIcon className="h-4 w-4 text-blue-500" />;
    }
    if (['pdf'].includes(ext)) {
      return <FileText className="h-4 w-4 text-red-500" />;
    }
    if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return <FileSpreadsheet className="h-4 w-4 text-green-500" />;
    }
    return <File className="h-4 w-4 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Merge comments and status history into a single timeline
  const timeline = [
    ...comments.map(comment => ({
      type: 'comment',
      data: comment,
      timestamp: comment.timestamp
    })),
    ...statusHistory.map(history => ({
      type: 'status',
      data: history,
      timestamp: history.timestamp
    }))
  ].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  if (timeline.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No activity yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {timeline.map((item, index) => {
        if (item.type === 'comment') {
          const comment = item.data;
          const isCurrentUser = currentUser && comment.author._id === currentUser.id;
          const isAdmin = comment.authorRole === 'admin';

          return (
            <div key={`comment-${index}`} className="flex space-x-3">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-medium ${
                  isAdmin ? 'bg-purple-500' : 'bg-blue-500'
                }`}>
                  {comment.author.name.charAt(0).toUpperCase()}
                </div>
              </div>

              {/* Comment Content */}
              <div className="flex-1 min-w-0">
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-gray-900">
                          {comment.author.name}
                        </span>
                        {isAdmin && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                            Admin
                          </span>
                        )}
                        {isCurrentUser && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                            You
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">
                        {formatTimestamp(comment.timestamp)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="px-4 py-3">
                    <p className="text-gray-900 whitespace-pre-wrap">{comment.message}</p>
                    
                    {/* Attachments */}
                    {comment.attachments && comment.attachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {comment.attachments.map((attachment, attIndex) => (
                          <a
                            key={attIndex}
                            href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/uploads/tasks/${attachment.filename}`}
                            download={attachment.originalName}
                            className="flex items-center space-x-3 p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors group"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {getFileIcon(attachment.originalName)}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {attachment.originalName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(attachment.size)}
                              </p>
                            </div>
                            <Download className="h-4 w-4 text-gray-400 group-hover:text-primary-600" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        } else {
          // Status change
          const history = item.data;
          const isAdmin = history.changedByRole === 'admin';
          const isLegacyRejected = history.status === 'incomplete';
          const statusForBadge = isLegacyRejected ? 'legacy-rejected' : history.status;
          const reviewDecisionForBadge = isLegacyRejected ? 'rejected' : 'none';

          return (
            <div key={`status-${index}`} className="flex space-x-3">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="h-5 w-5 text-gray-600" />
                </div>
              </div>
              
              <div className="flex-1 min-w-0 pt-2">
                <div className="text-sm">
                  <span className="font-semibold text-gray-900">
                    {history.changedBy.name}
                  </span>
                  {isAdmin && (
                    <span className="ml-1 px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                      Admin
                    </span>
                  )}
                  <span className="text-gray-600"> changed status to </span>
                  <StatusBadge
                    status={statusForBadge}
                    reviewDecision={reviewDecisionForBadge}
                    legacyRejected={isLegacyRejected}
                    className="ml-1"
                  />
                </div>
                {history.comment && (
                  <p className="mt-1 text-sm text-gray-600 italic">"{history.comment}"</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formatTimestamp(history.timestamp)}
                </p>
              </div>
            </div>
          );
        }
      })}
    </div>
  );
};

export default CommentThread;
